import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { logger } from '../../../core/utils/console';

import { FuseConfigService } from '@fuse/services/config.service';
import { fuseAnimations } from '@fuse/animations';
import { AuthService } from 'app/core/auth/auth.service';
import { OrgType } from 'app/core/api/gestacard.service';
import { Router, NavigationExtras, ActivatedRoute } from '@angular/router';
import { AlertsService } from 'app/shared/alerts.service';
import { OverlayService } from 'app/shared/overlay.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class LoginComponent implements OnInit {
    loginForm: FormGroup;
    emailCheckForm: FormGroup;
    sendCodeForm: FormGroup;

    isLoading = false;
    visiblePassword = false;
    emailCheck: Boolean = false;
    initProcess = false;

    rememberMe = false;

    orgs: OrgType[] = [];
    orgSelectionStep = false;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FormBuilder} _formBuilder
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _formBuilder: FormBuilder,
        private auth: AuthService,
        private router: Router,
        private sharedAlerts: AlertsService,
        private overlayService: OverlayService,
        private route: ActivatedRoute,
        private snackService: AlertsService,
        private location: Location
    ) {
        // Configure the layout
        this._fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                },
                footer: {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        var email = ``; 

        // Extrai o path sem query params para verificar se é webview
        const currentPath = this.router.url.split('?')[0];

        // Se for rota /webview, NÃO faz login aqui
        // O WebviewGuard já valida o token e o componente usa modo readonly
        if (currentPath.startsWith('/webview')) {
          logger.log('LoginComponent: Rota /webview detectada - ignorando processamento de key');
          this.initializeForms(email);
          return;
        }

        this.router.url.split('?').forEach(async (item) => {
            const params = new URLSearchParams(item);

            if (params.has('key')) {
              // Login via key parameter (deep link apenas - webview já foi filtrado acima)
              const token = params.get('key');
              
              logger.log('LoginComponent: Detectado login via key parameter (deep link)');
              this.overlayService.show();
              this.isLoading = true;

              try {
                await this.auth.LoginByKey(token);
                
                // Sucesso: limpa URL para não expor token
                this.cleanUrlFromToken();
                
                this.sharedAlerts.SnackMessage('Login realizado com sucesso!');
                
              } catch (error) {
                logger.error('LoginComponent: Erro no login via key', error);
                
                // Limpa URL mesmo em caso de erro
                this.cleanUrlFromToken();
                
                let errorMessage = 'Link inválido ou expirado';
                if (error && error.message) {
                  errorMessage = error.message;
                }
                
                this.sharedAlerts.SnackMessage(errorMessage);
                
                // Permite login manual
                this.initializeForms(email);
                
              } finally {
                this.overlayService.hide();
                this.isLoading = false;
              }

            } else {
                // Login manual normal
                if (localStorage.getItem(`extranetLogin`)) {
                    const recordedLogin = JSON.parse(localStorage.getItem(`extranetLogin`));
                    email = recordedLogin.email; 
                    // Senha não é mais armazenada por segurança
                }

                this.initializeForms(email);
            }
        })
    }

    /**
     * Inicializa os formulários de login
     */
    private initializeForms(email: string): void {
        this.loginForm = this._formBuilder.group({
            email: [email, [Validators.required, Validators.email]],
            password: ['', Validators.required]
        });

        this.emailCheckForm = this._formBuilder.group({
            email: [email, [Validators.required, Validators.email]],
        });

        this.sendCodeForm = this._formBuilder.group({
            email: [email, [Validators.required, Validators.email]],
        });
    }

    /**
     * Remove o parâmetro ?key= da URL para não expor token
     * Previne: logs, histórico, screenshots, analytics
     */
    private cleanUrlFromToken(): void {
        // Remove query params da URL sem recarregar página
        const urlWithoutParams = this.router.url.split('?')[0];
        this.location.replaceState(urlWithoutParams);
        logger.log('LoginComponent: URL limpa, token removido do histórico');
    }

    ChecarEmail() {

        this.emailCheck = false;

        if (this.emailCheckForm.controls.email.valid) {

            this.overlayService.show();

            this.auth.ChecarUsuario(this.emailCheckForm.controls.email.value).then((needCode) => {

                if (needCode) {
                    this.auth.EnviarCodigo(this.emailCheckForm.controls.email.value).then(() => {

                        var navigationExtras: NavigationExtras = {
                            state: {
                                email: this.emailCheckForm.controls.email.value
                            }
                        }; 

                        this.sharedAlerts.SnackMessage(`Crie um novo login para acessar o aplicativo.`);
                        this.router.navigateByUrl(`/auth/alter-password`, navigationExtras);

                    }).catch(() => {

                        this.emailCheck = needCode;
                        this.initProcess = true;   

                    }).finally(() => {

                        this.overlayService.hide();

                    })
                } else {
                    this.sharedAlerts.SnackMessage(`Faça seu login.`);
                    
                    this.loginForm.controls.email.setValue(this.emailCheckForm.controls.email.value);

                    this.emailCheck = needCode;
                    this.initProcess = true;   
                
                    this.overlayService.hide();
                }

            }).catch((error) => {
                var message = '';
                if (typeof error === 'string') message = error;
                if (error.hasOwnProperty('message') && typeof error.message === 'string') message = error.message;
                if (error.hasOwnProperty('message') && typeof error.message === 'object') message = error.message.error.error

                if (message == '') message = 'Erro ao verificar e-mail.';

                this.sharedAlerts.SnackMessage(message);
                this.emailCheck = false;
                this.initProcess = false;
                
                this.overlayService.hide();

            })

        }

    }

    EnviarCodigo() {
        this.overlayService.show();

        this.auth.EnviarCodigo(this.sendCodeForm.controls.email.value).then(() => {
            var navigationExtras: NavigationExtras = {
                state: {
                  email: this.sendCodeForm.controls.email.value
                }
            }; 

            this.sharedAlerts.SnackMessage(`Código enviado com sucesso!`);
            this.router.navigateByUrl(`/auth/alter-password`, navigationExtras);
        }).catch(() => {
            this.sharedAlerts.SnackMessage(`Erro ao enviar código!`);
        }).finally(() => {
            this.overlayService.hide();
        })
    }

    Login() {
        if (this.loginForm.valid || true) {
            this.overlayService.show();

            const email = this.loginForm.controls.email.value;
            const senha = this.loginForm.controls.password.value;

            this.auth.GetLoginOrgs(email, senha).then((orgs) => {

                if (orgs.length === 1) {
                    this._finalizarLogin(email, senha, orgs[0].id);
                } else {
                    this.orgs = orgs;
                    this.orgSelectionStep = true;
                    this.overlayService.hide();
                }

            }).catch((error) => {
                var message = '';
                if (typeof error === 'string') message = error;
                if (error && error.hasOwnProperty('message') && typeof error.message === 'string') message = error.message;
                if (error && error.hasOwnProperty('message') && typeof error.message === 'object') message = error.message.error.error;

                if (message == '') message = 'Erro ao verificar organizações.';

                this.sharedAlerts.SnackMessage(message);
                this.overlayService.hide();
            });
        }
    }

    SelecionarOrg(orgId: string) {
        const email = this.loginForm.controls.email.value;
        const senha = this.loginForm.controls.password.value;

        this.overlayService.show();
        this._finalizarLogin(email, senha, orgId);
    }

    VoltarParaSenha() {
        this.orgSelectionStep = false;
        this.orgs = [];
    }

    private _finalizarLogin(email: string, senha: string, orgId: string) {
        this.auth.Login(email, senha, orgId).then(() => {

                this.sharedAlerts.SnackMessage(`Login feito com sucesso!`);

                // Armazena apenas o email (nunca a senha) para conveniência do usuário
                if(this.rememberMe) {
                    localStorage.setItem(`extranetLogin`, JSON.stringify({ email }));
                } else {
                    localStorage.removeItem(`extranetLogin`);
                }

            }).catch((error) => {
                var message = '';
                if (typeof error === 'string') message = error;
                if (error.hasOwnProperty('message') && typeof error.message === 'string') message = error.message;
                if (error.hasOwnProperty('message') && typeof error.message === 'object') message = error.message.error.error;

                if (message == '') message = 'Erro ao fazer login.';

                this.sharedAlerts.SnackMessage(message);
                this.orgSelectionStep = false;

            }).finally(() => {

                this.overlayService.hide();

            });
    }

    EsqueceuASenha() {
        const email = this.loginForm.controls.email.value;
        if (email && email != '') {
            const extras: NavigationExtras = {
                state: {
                    email
                }
            };
            this.router.navigate(['/auth/esqueceu-senha'], extras);

        } else {
            this.router.navigateByUrl('/auth/esqueceu-senha');
        }
    }

    async register() {
        this.auth.Login('', '').finally(() => {
            this.router.navigate(['/auth/signup']);
        })

        // window.open('https://gestacard.app/cadastre-se', "_blank", "closebuttoncaption=Cancelar,hidenavigationbuttons=yes,footer=yes,footercolor=#47b377,closebuttoncolor=#FFFFFF,location=no");

    }

    setVisiblePassword(visiblePassword: boolean) {
        this.visiblePassword = visiblePassword;
    }

}
